<template>
  <SelectRoot
    :model-value="campaignId ? String(campaignId) : undefined"
    class="disabled:opacity-50"
    @update:model-value="campaignId = Number($event)"
  >
    <SelectTrigger
      class="w-full rounded-lg text-[15px] font-medium leading-[18px]"
      aria-label="Customise options"
    >
      <Box class="bg-white">
        <div
          :style="{
            '--campaign-color': campaignId ? colors[campaignId] : undefined,
          }"
          class="flex min-h-14 w-full items-center justify-between gap-1 rounded-[inherit] bg-[--campaign-color] p-3"
        >
          <SelectValue
            class="line-clamp-1 text-left"
            :class="!campaignId && 'text-gray-400'"
            placeholder="Choose a campaign..."
          />
          <IconsArrowUpSimple class="h-3 w-3 shrink-0 rotate-180" />
        </div>
      </Box>
    </SelectTrigger>
    <SelectPortal>
      <SelectContent class="z-[100]">
        <Box class="overflow-hidden bg-white">
          <SelectViewport
            :style="{
              '--campaign-color': campaignId ? colors[campaignId] : undefined,
            }"
            class="bg-[--campaign-color]"
          >
            <template v-for="(option, index) in options" :key="index">
              <SelectItem
                class="cursor-pointer p-3 outline-none transition-colors data-[highlighted]:bg-navy data-[highlighted]:text-white"
                :value="String(option.value)"
              >
                <SelectItemText class="text-[15px] font-medium leading-[18px]">
                  <div class="flex items-center gap-3">
                    <img
                      class="inline-block h-8 w-8"
                      :src="emojis[option.value]"
                    />
                    <div class="flex flex-col gap-0.5">
                      <span>{{ option.text }}</span>
                      <span class="text-xs leading-none">
                        {{ option.organization }}
                      </span>
                    </div>
                  </div>
                </SelectItemText>
              </SelectItem>
              <div
                v-if="options && index !== options.length - 1"
                class="w-full border-b border-dashed border-navy"
              />
            </template>
          </SelectViewport>
        </Box>
      </SelectContent>
    </SelectPortal>
  </SelectRoot>
</template>

<script setup lang="ts">
import { colors, Campaign, emojis } from "@/lib/campaigns";
import { useCampaigns } from "~/domains/campaigns/composables";

const props = defineProps<{
  allowed?: Campaign[];
  amount?: number;
}>();

const { data: campaigns, isLoading, isError } = useCampaigns();

const campaignId = defineModel<Campaign>();

const impactFormatter = new Intl.NumberFormat("en-US", {
  style: "decimal",
  maximumFractionDigits: 1,
});

const options = computed(() => {
  if (isLoading.value || isError.value) return undefined;
  if (!campaigns.value) return undefined;

  const campaignIds = [
    Campaign.TFTF,
    Campaign.OVI,
    Campaign.PF,
    Campaign.SARTHAK,
    Campaign.CLEANHUB,
    Campaign.REEFGEN,
  ];

  let _options: Array<{ value: Campaign; text: string; organization: string }> =
    [];
  if (typeof props.amount === "undefined") {
    _options = campaignIds.map((id) => {
      const campaign = campaigns.value?.find((c) => c.campaignId === id);
      if (!campaign) throw new Error(`Campaign not found: ${id}`);
      return {
        value: id,
        text: campaign.title,
        organization: campaign.organizationName,
      };
    });
  } else {
    const amount = props.amount;
    _options = campaignIds.map((id) => {
      const campaign = campaigns.value?.find((c) => c.campaignId === id);
      if (!campaign) throw new Error(`Campaign not found: ${id}`);

      let text;
      switch (id) {
        case Campaign.TFTF:
          text = `Trees (plants ${impactFormatter.format(amount * campaign.dollarRatio)} trees)`;
          break;
        case Campaign.OVI:
          text = `Ocean (removes ${impactFormatter.format(amount * campaign.dollarRatio)} lbs of ocean plastic pollution)`;
          break;
        case Campaign.PF:
          text = `Ocean (removes ${impactFormatter.format(amount * campaign.dollarRatio)} lbs of ocean-bound plastic pollution)`;
          break;
        case Campaign.SARTHAK:
          text = `Education (funds ${impactFormatter.format(amount * campaign.dollarRatio)} days of education for children living in poverty)`;
          break;
        case Campaign.CLEANHUB:
          text = `Ocean (removes ${impactFormatter.format(amount * campaign.dollarRatio)} lbs of plastic from coastal areas)`;
          break;
        case Campaign.REEFGEN:
          text = `Seagrass (plants ${impactFormatter.format(amount * campaign.dollarRatio)} seagrass seeds)`;
          break;
        default:
          throw new Error(`Unknown campaign: ${id}`);
      }
      return {
        value: id,
        text,
        organization: campaign.organizationName,
      };
    });
  }

  const allowed = props.allowed;
  if (allowed) return _options.filter((o) => allowed.includes(o.value));

  return _options;
});
</script>
